// pages/[city]/index.tsx
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef } from "react";
import Head from "next/head";
import { GetStaticProps, GetStaticPaths } from "next";
import { GET_ALL_TURFS } from "../../src/utils/queries";
import { transformTurfData } from "../../src/utils/transformTurfData";
import TileContainer from "../../src/page/HomePage/TileContainer";
// SEO imports
import CityStructuredData from "../../src/components/SEO/CityStructuredData";
import CityContentSection from "../../src/components/SEO/CityContentSection";
import {
  generateCityPageTitle,
  generateCityMetaDescription,
  generateCityKeywords,
  getCityCanonicalUrl,
  generateAreaPageTitle,
  generateAreaMetaDescription,
  generateAreaKeywords,
  getAreaCanonicalUrl,
  formatCityName,
  generateListingItemListSchema,
  generateListingFaqSchema,
} from "../../src/utils/seoHelpers";
import {
  createDynamicCityData,
  getCityData,
  getCanonicalCitySlug,
} from "../../src/config/cityData";
import { CityData } from "../../src/types/city.types";
import { useTurfContext } from "../../src/contexts/TurfContext";
import { getAvailableSportTabs } from "../../src/utils/sportTabs";
import { seoConfig } from "../../src/config/seo.config";
import {
  fetchCityCatalog,
  fetchGraphQL,
  resolveApiCityData,
} from "../../src/utils/cityApi";
import { hasVisiblePitches } from "../../src/utils/turfVisibility";

// Generate static paths for all cities at build time
export const getStaticPaths: GetStaticPaths = async () => {
  const cityCatalog = await fetchCityCatalog();
  const paths = cityCatalog.map(city => ({
    params: { city: city.slug }
  }));
  
  return {
    paths,
    fallback: 'blocking' // Generate new cities on-demand
  };
};

// ISR: Fetch data at build time and revalidate every hour
export const getStaticProps: GetStaticProps = async (context) => {
  const rawCitySlug = Array.isArray(context.params?.city) 
    ? context.params.city[0] 
    : context.params?.city;
  const citySlug = rawCitySlug ? getCanonicalCitySlug(rawCitySlug) : rawCitySlug;
  
  // Validate city slug
  if (!citySlug || citySlug === 'noData') {
    return { notFound: true };
  }

  // Alternate spellings and casing variants (bombay, Mumbai, hubballi, ...) must not
  // serve a duplicate 200 page that cross-canonicals to the real slug.
  if (rawCitySlug !== citySlug) {
    return {
      redirect: {
        destination: `/${citySlug}`,
        permanent: true,
      },
    };
  }
  
  // Get city data from cityDataMap (if exists)
  let cityData = getCityData(rawCitySlug as string);
  
  // Fetch turf data from API
  let turfData: any[] = [];
  let hasError = false;
  let apiCityName = citySlug; // Default to slug if cityData not found
  
  try {
    if (!cityData) {
      cityData = await resolveApiCityData(citySlug as string);
    }

    // If cityData exists, use its name; otherwise try slug as city name
    // This supports dynamic cities from admin panel
    if (cityData) {
      apiCityName = cityData.name;
    } else {
      // City not in cityDataMap - try slug as city name (capitalize first letter)
      apiCityName = formatCityName(citySlug as string);
    }

    const result = await fetchGraphQL<{
      turfs?: {
        turfs?: any[];
      };
    }>(GET_ALL_TURFS, {
      city: apiCityName,
      verificationStatus: "VERIFIED",
    });

    turfData = (result?.turfs?.turfs || [])
      .filter((turf: any) => turf?.hideTurf !== true && hasVisiblePitches(turf))
      .map((turf: any) =>
        transformTurfData(turf, cityData?.name || apiCityName)
      );

    // If city not in cityDataMap but has turfs, create dynamic city data
    if (!cityData && turfData.length > 0) {
      cityData = createDynamicCityData({
        name: turfData[0]?.data?.city || apiCityName,
      });
    }
  } catch (error) {
    console.error("Error fetching turf data for", citySlug, ":", error);
    hasError = true;
  }
  
  // If city cannot be resolved from config/API, or no turfs exist, return 404
  if (!cityData || (!hasError && turfData.length === 0)) {
    return { notFound: true };
  }
  
  return {
    props: {
      citySlug: citySlug as string,
      cityData,
      initialTurfData: turfData,
      turfCount: turfData.length,
      hasError,
    },
    revalidate: 180, // Revalidate every 3 minutes (ISR) - Faster data updates while maintaining performance
  };
};

interface CityPageProps {
  citySlug: string | null;
  cityData: CityData | null;
  initialTurfData: any[];
  turfCount: number;
  hasError: boolean;
  search?: string;
  setCity?: Dispatch<SetStateAction<string>>;
  activeTab?: string;
  handleTabChange?: (key: string) => void;
  areaSlug?: string | null;
  areaName?: string | null;
}

export default function CityPage({ 
  citySlug, 
  cityData,
  initialTurfData,
  turfCount,
  hasError,
  search, 
  setCity, 
  activeTab, 
  handleTabChange,
  areaSlug,
  areaName,
}: CityPageProps) {
  const displayCity = citySlug?.toLowerCase().trim() || '';
  const cityName = cityData ? cityData.name : formatCityName(displayCity);
  const isAreaPage = !!(areaSlug && areaName);
  const { setAvailableSportTabs } = useTurfContext();

  const availableSportTabs = useMemo(
    () => getAvailableSportTabs(initialTurfData),
    [initialTurfData]
  );

  useEffect(() => {
    setAvailableSportTabs(availableSportTabs);
    return () => setAvailableSportTabs([]);
  }, [availableSportTabs, setAvailableSportTabs]);

  // A `?tab=` value can point at a sport this city no longer has (stale link,
  // hand-typed URL); drop back to the first tab that does exist here. Corrected
  // at most once per tab value, so this can never trade updates with the tab
  // sync in _app and blow the update depth.
  const correctedTabRef = useRef<string | null>(null);
  useEffect(() => {
    if (!handleTabChange || !activeTab || availableSportTabs.length === 0) return;
    if ((availableSportTabs as string[]).includes(activeTab)) return;
    if (correctedTabRef.current === activeTab) return;
    correctedTabRef.current = activeTab;
    handleTabChange(availableSportTabs[0]);
  }, [activeTab, availableSportTabs, handleTabChange]);

  const pageTitle = isAreaPage
    ? generateAreaPageTitle(areaName!, cityName)
    : generateCityPageTitle(cityName);
  const pageDescription = isAreaPage
    ? generateAreaMetaDescription(areaName!, cityName, turfCount)
    : generateCityMetaDescription(cityName, turfCount);
  const keywords = isAreaPage
    ? generateAreaKeywords(areaName!, displayCity)
    : generateCityKeywords(displayCity);
  const canonicalUrl = isAreaPage
    ? getAreaCanonicalUrl(displayCity, areaSlug!)
    : getCityCanonicalUrl(displayCity);
  const geoPlacename = isAreaPage
    ? `${areaName}, ${cityName}, ${cityData?.state || 'India'}`
    : `${cityName}, ${cityData?.state || 'India'}`;
  const baseUrl = seoConfig.baseUrl;
  const topTurfNames = Array.from(
    new Set(
      initialTurfData
        .map((turf: any) => turf?.name)
        .filter(Boolean)
    )
  ).slice(0, 6);
  const itemListSchema = generateListingItemListSchema({
    citySlug: displayCity,
    areaName: isAreaPage ? areaName : undefined,
    turfNames: topTurfNames,
  });
  const faqSchema = generateListingFaqSchema({
    citySlug: displayCity,
    areaName: isAreaPage ? areaName : undefined,
    turfCount,
  });

  const seoContent = (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${baseUrl}/TBlogo.png`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="TurfBooking.in" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${baseUrl}/TBlogo.png`} />
        
        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="IN" />
        <meta name="geo.country" content="India" />
        <meta name="geo.placename" content={geoPlacename} />
        {itemListSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
          />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </Head>
      
      {/* Structured Data Schemas */}
      <CityStructuredData
        city={displayCity}
        turfCount={turfCount}
        areaName={isAreaPage ? areaName : undefined}
        areaSlug={isAreaPage ? areaSlug : undefined}
      />
    </>
  );

  // Handle error state
  if (hasError) {
    return (
      <>
        {seoContent}
        <div className="loader-container">
          <p>Unable to load turf data. Please try again later.</p>
        </div>
      </>
    );
  }

  // Empty city listings: keep a simple message. Empty area URLs are 404'd in
  // getStaticProps so they never reach here as indexable 200 pages.
  if ((!initialTurfData || initialTurfData.length === 0) && !isAreaPage) {
    return (
      <>
        {seoContent}
        <div className="loader-container">
          <p>No turfs available in {cityName}</p>
        </div>
      </>
    );
  }

  // Render page with pre-loaded data (ISR)
  return (
    <>
      {seoContent}
      
      {/* Main Content - Data is pre-loaded from ISR */}
      <TileContainer
        city={displayCity}
        cityId={cityData?.id || null}
        search={search || ""}
        setCity={setCity || (() => {})}
        turfData={initialTurfData}
        isLoading={false}
        activeTab={activeTab || "turfs"}
        handleTabChange={handleTabChange}
      />

      {/* Visible landing content for stronger query relevance and CTR support */}
      <CityContentSection
        city={displayCity}
        turfCount={turfCount}
        areaName={isAreaPage ? areaName : undefined}
        topTurfNames={topTurfNames}
      />
    </>
  );
}
